import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import { Check, Warning } from './Icons'

interface ToastItem {
  id: number
  message: string
  tone: 'success' | 'error'
}
interface ToastApi {
  success: (message: string) => void
  error: (message: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const nextId = useRef(0)

  const push = useCallback((message: string, tone: 'success' | 'error') => {
    const id = nextId.current++
    setToasts((t) => [...t, { id, message, tone }])
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4200)
  }, [])

  const api = useMemo<ToastApi>(
    () => ({
      success: (m) => push(m, 'success'),
      error: (m) => push(m, 'error'),
    }),
    [push],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-5 z-50 flex flex-col items-center gap-2 px-4 sm:items-end sm:px-6" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="pointer-events-auto flex max-w-sm items-center gap-3 rounded-[14px] bg-ink-900 py-3 pl-3 pr-4 text-[13.5px] text-paper shadow-lift ring-1 ring-white/10 animate-scale-in"
          >
            <span className={`grid size-6 shrink-0 place-items-center rounded-full ${t.tone === 'success' ? 'bg-live-500/20 text-live-400' : 'bg-danger-500/25 text-danger-100'}`}>
              {t.tone === 'success' ? <Check size={13} strokeWidth={2} /> : <Warning size={13} strokeWidth={2} />}
            </span>
            <span className="leading-snug">{t.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- small provider+hook pair kept in one file on purpose
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within a ToastProvider')
  return ctx
}
